import styles from "./VolunteerPersonalInfo.module.scss";
import { useTranslation } from "../../i18n";
import { useContext, useEffect, useState } from "react";
import { Button, TextField } from "@mui/material";
import { Dropzone } from "../Dropzone/Dropzone";
import { AuthContext } from "../../context/AuthContext";
import { InputAddress } from "../InputAddress/InputAddress";
import {
  InputMunicipality,
  MunicipalitySuggestion,
} from "../InputMunicipality/InputMunicipality";
import { fetcher } from "../../utils/fetcher";

import { useAlert } from "../../context/AlertContext";
import { FileWithPreview } from "../../types/common";

type VolunteerDataForm = {
  first_name: string;
  last_name: string;
  phone_number: string;
  date_of_birth: string;
  permanent_address: string;
  locations: MunicipalitySuggestion[];
};

export const VolunteerPersonalInfo = () => {
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  const { currentUser, editUser, fetchVolunteer } = authContext;
  const { setAlert } = useAlert();
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [selectedTowns, setSelectedTowns] = useState<MunicipalitySuggestion[]>(
    [],
  );
  const [volunteerData, setVolunteerData] = useState<VolunteerDataForm>({
    first_name: (currentUser?.userData as any)?.first_name || "",
    last_name: (currentUser?.userData as any)?.last_name || "",
    phone_number: (currentUser?.userData as any)?.phone_number || "",
    date_of_birth: (currentUser?.userData as any)?.date_of_birth || "",
    permanent_address: (currentUser?.userData as any)?.permanent_address || "",
    locations: selectedTowns,
  });

  useEffect(() => {
    if (currentUser) {
      const userData = currentUser.userData as any;
      const locations: MunicipalitySuggestion[] = Array.isArray(
        userData?.locations,
      )
        ? userData.locations.filter(
            (l: any) => l && typeof l === "object" && l.name,
          )
        : [];
      setVolunteerData({
        first_name: userData?.first_name || "",
        last_name: userData?.last_name || "",
        phone_number: userData?.phone_number || "",
        date_of_birth: userData?.date_of_birth || "",
        permanent_address: userData?.permanent_address || "",
        locations,
      });
      setSelectedTowns(locations);
    }
  }, [currentUser]);

  useEffect(() => {
    setVolunteerData((prevData) => ({
      ...prevData,
      locations: selectedTowns,
    }));
  }, [selectedTowns]);

  const handleEditConfirm = () => {
    if (editMode) {
      // If date_of_birth is filled and step is not 4, update to step 4
      const shouldUpdateStep =
        volunteerData.date_of_birth &&
        (currentUser?.userData as any)?.step_of_registration !== 4;

      const dataToUpdate = shouldUpdateStep
        ? { ...volunteerData, step_of_registration: 4 }
        : volunteerData;

      editUser(dataToUpdate, "volunteer");
      setEditMode(false);
      return;
    }
    setEditMode(true);
  };

  const stepOfRegistration = (currentUser?.userData as any)
    ?.step_of_registration;
  const isProfileIncomplete =
    stepOfRegistration !== 4 && !volunteerData.date_of_birth;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setVolunteerData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleImageUpload = async (file: File | undefined) => {
    if (!file || !currentUser) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await fetcher({
        url: `/volunteer/upload-profile-image/${currentUser.userId}`,
        method: "POST",
        body: formData,
        noContentType: true,
      });
      fetchVolunteer(currentUser.userId);
      setFiles([]);
    } catch (error) {
      setAlert(
        true,
        "error",
        t("orgProfile.alerts.imageUploadFailed"),
      );
      console.error("Error uploding image:", error);
    }
  };

  const handleImageDelete = async () => {
    if (!currentUser) return;

    try {
      await fetcher({
        url: `/volunteer/delete-profile-image/${currentUser.userId}`,
        method: "DELETE",
      });
      fetchVolunteer(currentUser.userId);
    } catch (error) {
      setAlert(
        true,
        "error",
        t("orgProfile.alerts.imageDeleteFailed"),
      );
      console.error("Error deleting image:", error);
    }
  };

  return (
    <div className={styles.personalInfo}>
      <div className={styles.form}>
        <div className={styles.flex}>
          <TextField
            id="first_name"
            type="text"
            label={t("volunteer.firstName")}
            variant="outlined"
            size="small"
            fullWidth
            value={volunteerData.first_name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(e)
            }
            disabled={!editMode}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            id="last_name"
            type="text"
            label={t("volunteer.lastName")}
            variant="outlined"
            size="small"
            fullWidth
            value={volunteerData.last_name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(e)
            }
            disabled={!editMode}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </div>
        <TextField
          id="phone_number"
          type="tel"
          label={t("volunteer.phone")}
          variant="outlined"
          size="small"
          fullWidth
          value={volunteerData.phone_number}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
          disabled={!editMode}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <div className={styles.dateOfBirthSection}>
          {isProfileIncomplete && (
            <p className={styles.dateOfBirthNote}>
              {t("volunteer.dobNoticeStart")}{" "}
              <strong>{t("volunteer.dobNoticeField")}</strong>.
              <br />
              <span className={styles.smallText}>
                {t("volunteer.dobNoticeHint")}
              </span>
            </p>
          )}
          <TextField
            id="date_of_birth"
            type="date"
            label={t("volunteer.dateOfBirth")}
            variant="outlined"
            size="small"
            fullWidth
            value={volunteerData.date_of_birth}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(e)
            }
            disabled={!editMode}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </div>
        <InputAddress
          editMode={editMode}
          label={t("volunteer.documentAddress")}
          address={{ address_text: volunteerData.permanent_address }}
          onAddressChange={(value) =>
            setVolunteerData((prevData) => ({
              ...prevData,
              permanent_address: value || "",
            }))
          }
        />
        <InputMunicipality
          editMode={editMode}
          selectedTowns={selectedTowns}
          onTownsChange={setSelectedTowns}
          label={t("volunteer.helpLocations")}
        />
        <div className={styles.flex}>
          <Button variant="contained" onClick={() => handleEditConfirm()}>
            {editMode ? t("common.confirm") : t("orgProfile.edit")}
          </Button>
        </div>
      </div>
      <div className={styles.profileImage}>
        <p className={styles.subtitle}>{t("volunteer.profilePhoto")}</p>
        <div className={styles.flex}>
          <Dropzone files={files} setFiles={setFiles} />
          {(currentUser?.userData as any)?.profile_image && (
            <div className={styles.imageContainer}>
              <button className={styles.delete} onClick={handleImageDelete}>
                {t("common.remove")}
              </button>
              <img
                src={(currentUser?.userData as any)?.profile_image}
                alt={t("volunteer.profilePhotoAlt")}
                className={styles.image}
                style={{ maxWidth: 120, marginBottom: 8 }}
              />
            </div>
          )}
        </div>
        <Button
          variant="contained"
          onClick={() => handleImageUpload(files[0])}
          disabled={!files.length}
          style={{ marginTop: 8 }}
        >
          {t("orgProfile.uploadImage")}
        </Button>
      </div>
    </div>
  );
};
